import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService } from 'api';
import type { ProjectMember, WorkspaceRoleString } from 'api';
import { WorkspaceRoleLabels } from 'api';
import { ChevronDown } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable } from 'shared/components/DataTable';
import { queryKeys } from 'shared/constants/queryKeys';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  AddMembersRow,
  DeleteMenuItem,
  FILTER_FIELD_TEXT,
  MemberCellAvatar,
  MemberCountBadge,
  MemberPrimaryText,
  MemberRowBox,
  MemberSearchBox,
  MemberSearchIconWrap,
  MemberSearchInput,
  MemberSecondaryText,
  MembersCardHeader,
  MembersTitleGroup,
  MembersTitleText,
  MemberStatusBadge,
  ReassignMenuItem,
  RoleFilterSelect,
  RolePill,
  TabCardShell,
} from '../projects.styles';

import AddMembersDropdown from './AddMembersDropdown';

interface Props {
  projectId: string;
}

const ROLE_OPTIONS: WorkspaceRoleString[] = ['Owner', 'Admin', 'ProjectManager', 'TeamMember'];

const ProjectAccessTab: React.FC<Props> = ({ projectId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { canAssignProjectMembers } = usePermissions();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | WorkspaceRoleString>('all');

  const [memberMenu, setMemberMenu] = useState<{
    anchorEl: HTMLElement | null;
    member: ProjectMember | null;
  }>({ anchorEl: null, member: null });

  const openMemberMenu = (e: React.MouseEvent<HTMLElement>, member: ProjectMember) => {
    e.stopPropagation();
    setMemberMenu({ anchorEl: e.currentTarget, member });
  };
  const closeMemberMenu = () => setMemberMenu({ anchorEl: null, member: null });

  const { data: projectMembers = [], isLoading: membersLoading } = useQuery<ProjectMember[]>({
    queryKey: queryKeys.projects.members(projectId),
    queryFn: () => projectService.getProjectMembers(projectId).then((r) => r.data.data),
  });

  const activeAssignedUserIds = useMemo(
    () => new Set(projectMembers.filter((m) => m.projectMemberStatus === 'Active').map((m) => m.userId)),
    [projectMembers],
  );

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projectMembers.filter((m) => {
      const matchesSearch =
        !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
      const matchesRole = roleFilter === 'all' || m.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [projectMembers, search, roleFilter]);

  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const removeMutation = useMutation({
    mutationFn: (userId: string) => {
      setRemovingUserId(userId);
      return projectService.removeProjectMember(projectId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.list() });
      enqueueSnackbar('Member removed', { variant: 'success' });
      setRemovingUserId(null);
      closeMemberMenu();
    },
    onError: () => {
      enqueueSnackbar('Failed to remove member', { variant: 'error' });
      setRemovingUserId(null);
      closeMemberMenu();
    },
  });

  const reassignMutation = useMutation({
    mutationFn: (userId: string) => projectService.addProjectMember(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.list() });
      enqueueSnackbar('Member reassigned', { variant: 'success' });
      closeMemberMenu();
    },
    onError: () => {
      enqueueSnackbar('Failed to reassign member', { variant: 'error' });
      closeMemberMenu();
    },
  });

  const columns = useMemo((): DataTableColumn<ProjectMember>[] => {
    const cols: DataTableColumn<ProjectMember>[] = [
      {
        id: 'name',
        label: 'Name',
        render: (member) => {
          const isRemoving = removingUserId === member.userId;
          const isDeactivated = member.workspaceMembershipStatus === 'Inactive';
          return (
            <MemberRowBox removing={isRemoving}>
              <MemberCellAvatar src={member.avatarUrl} alt={member.name}>
                {member.name.charAt(0).toUpperCase()}
              </MemberCellAvatar>
              <div>
                <MemberPrimaryText variant="body2" removing={isRemoving || isDeactivated}>
                  {member.name}
                </MemberPrimaryText>
                <MemberSecondaryText variant="caption" removing={isRemoving || isDeactivated}>
                  {member.email}
                </MemberSecondaryText>
              </div>
            </MemberRowBox>
          );
        },
      },
      {
        id: 'role',
        label: 'Role',
        render: (member) => (
          <RolePill roleKind={member.role} label={WorkspaceRoleLabels[member.role]} size="small" />
        ),
      },
      {
        id: 'status',
        label: 'Status',
        render: (member) => {
          const exited = member.workspaceMembershipStatus === 'Inactive';
          const inactive = !exited && member.projectMemberStatus === 'Inactive';
          const label = exited ? 'Exited' : inactive ? 'Inactive' : 'Active';
          return (
            <MemberStatusBadge active={!exited && !inactive} label={label} memberLabel={label} size="small" />
          );
        },
      },
    ];

    if (canAssignProjectMembers) {
      cols.push({
        id: 'actions',
        label: '',
        width: 48,
        align: 'right',
        render: (member) => {
          const exited = member.workspaceMembershipStatus === 'Inactive';
          if (exited) return null;
          return (
            <IconButton size="small" onClick={(e) => openMemberMenu(e, member)}>
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          );
        },
      });
    }

    return cols;
  }, [canAssignProjectMembers, removingUserId]);

  const menuMember = memberMenu.member;
  const isMenuMemberInactive =
    menuMember?.projectMemberStatus === 'Inactive' &&
    menuMember?.workspaceMembershipStatus !== 'Inactive';

  return (
    <TabCardShell variant="outlined">
      <MembersCardHeader>
        <MembersTitleGroup>
          <MembersTitleText>Members</MembersTitleText>
          <MemberCountBadge>{projectMembers.length}</MemberCountBadge>
        </MembersTitleGroup>

        <MemberSearchBox>
          <MemberSearchIconWrap>
            <SearchIcon sx={{ fontSize: 16 }} />
          </MemberSearchIconWrap>
          <MemberSearchInput
            placeholder="Search members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </MemberSearchBox>

        <RoleFilterSelect
          size="small"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as 'all' | WorkspaceRoleString)}
          IconComponent={(props) => <ChevronDown {...props} size={16} strokeWidth={2} />}
        >
          <MenuItem value="all">All roles</MenuItem>
          {ROLE_OPTIONS.map((role) => (
            <MenuItem key={role} value={role}>
              {WorkspaceRoleLabels[role]}
            </MenuItem>
          ))}
        </RoleFilterSelect>

        {canAssignProjectMembers && (
          <AddMembersRow>
            <AddMembersDropdown projectId={projectId} assignedUserIds={activeAssignedUserIds} />
          </AddMembersRow>
        )}
      </MembersCardHeader>

      <DataTable<ProjectMember>
        columns={columns}
        rows={filteredMembers}
        getRowId={(m) => m.userId}
        loading={membersLoading}
        emptyMessage={
          search || roleFilter !== 'all'
            ? 'No members match your search.'
            : 'No members assigned to this project.'
        }
        headerBgColor="#F3F0FF"
        headerFontSize="0.625rem"
        headerTextColor={FILTER_FIELD_TEXT}
        headerHeight={36}
        headerPaddingY={0}
        rowHoverColor="rgba(110, 79, 247, 0.03)"
        rowHeight={56}
        cellPaddingX={20}
        cellPaddingY={0}
        containerSx={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}
      />

      <Menu
        anchorEl={memberMenu.anchorEl}
        open={Boolean(memberMenu.anchorEl)}
        onClose={closeMemberMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isMenuMemberInactive ? (
          <ReassignMenuItem
            onClick={() => menuMember && reassignMutation.mutate(menuMember.userId)}
            disabled={reassignMutation.isPending}
          >
            <ListItemText>Reassign to project</ListItemText>
          </ReassignMenuItem>
        ) : (
          <DeleteMenuItem
            onClick={() => menuMember && removeMutation.mutate(menuMember.userId)}
            disabled={removeMutation.isPending}
          >
            <ListItemText>Remove from project</ListItemText>
          </DeleteMenuItem>
        )}
      </Menu>
    </TabCardShell>
  );
};

export default ProjectAccessTab;
